import { all, call, put, takeEvery, takeLeading } from 'redux-saga/effects';
import { ToastManager } from '../../components/Toast/ToastManager';
import {
    candidateCallEndFailure,
    candidateCallEndSuccess,
    candidateCallStartFailure,
    candidateCallStartSuccess,
    createCodeSubmissionFailure,
    createCodeSubmissionSuccess,
    fullFaceDetectionFailure,
    fullFaceDetectionNewFailure,
    fullFaceDetectionNewSuccess,
    fullFaceDetectionSuccess,
    getInterviewFailure,
    getInterviewSuccess,
    lookingForwardFailure,
    lookingForwardSuccess,
    practiceQuestionFailure,
    practiceQuestionSuccess,
    practiceQuestionUpdateFailure,
    practiceQuestionUpdateSuccess,
    resendOtpRequestFailure,
    resendOtpRequestSuccess,
    submitCodingQuestionFailure,
    submitCodingQuestionSuccess,
    submitTranscribeFailure,
    submitTranscribeSuccess,
    verifyCandidateKeyFailure,
    verifyCandidateKeySuccess,
    verifyRoomFailure,
    verifyRoomSuccess,
    updateCandidateSuccess,
    updateCandidateFailure,
    getQuestionSuccess,
    getQuestionFailure,
    fetchScreenShotImageSuccess,
    fetchScreenShotImageFailure,
    postConsentFailure,
    postConsentSuccess,
    liveKitTokenSuccess,
    liveKitTokenFailure,
    getSessionTokenSuccess,
    getSessionTokenFailure,
} from '../action';
import {
    CANDIDATE_CALL_END,
    CANDIDATE_CALL_START,
    CREATE_CODE_SUBMISSION,
    FULL_FACE_DETECTION,
    FULL_FACE_DETECTION_NEW,
    GET_INTERVIEW,
    LOOKING_FORWARD,
    PRACTICE_QUESTION,
    PRACTICE_QUESTION_UPDATE,
    RESEND_OTP_REQUEST,
    SUBMIT_CODING_QUESTION,
    SUBMIT_TRANSCRIBE,
    VERIFY_CANDIDATE_KEY,
    VERIFY_ROOM,
    UPDATE_CANDIDATE,
    GET_QUESTION,
    FETCH_SCREENSHOT_IMAGE,
    POST_CONSENT,
    LIVE_KIT_TOKEN,
    GET_SESSION_TOKEN,
} from '../action/types';
import INTERVIEW from '../../utils/api/interview';
import VIDEO from '../../utils/api/video';
import AUTH from '../../utils/api/auth';
import MockInterview from '../../utils/api/MockInterview';
import { GENERAL_ERROR } from '../../common/constants';

function* getSessionTokenRequest(action) {
    try {
        const { keys, callback } = action.payload;
        const res = yield MockInterview.post(`token/session`, { keys });
        if (res.status === 200 || res.status === 201) {
            yield put(getSessionTokenSuccess());
            if (callback) yield call(callback, res?.data?.result);
        }
    } catch (e) {
        yield put(getSessionTokenFailure());
    }
}

function* liveKitTokenRequest(action) {
    try {
        const { roomId, mode, role } = action.payload;
        const res = yield INTERVIEW.get(`auth/getToken/${roomId}/${mode}/${role}`);
        if (res.status === 200 || res.status === 201) {
            yield put(liveKitTokenSuccess());
            yield call(action.payload.callback, res?.data?.result);
        }
    } catch (e) {
        yield put(liveKitTokenFailure());
    }
}

function* practiceQuestionRequest(action) {
    try {
        const res = yield INTERVIEW.get(`interviews/practiceInterview/${action.payload.roomId}`);
        if (res.status === 200 || res.status === 201) {
            yield put(practiceQuestionSuccess());
            yield call(action.payload.callback, res?.data?.result?.practiceInterview);
        }
    } catch (e) {
        yield put(practiceQuestionFailure());
    }
}

function* practiceQuestionUpdateRequest(action) {
    try {
        const res = yield INTERVIEW.post(`interviews/practiceInterview/${action.payload.roomId}`, {});
        if (res.status === 200 || res.status === 201) {
            yield put(practiceQuestionUpdateSuccess());
        }
    } catch (e) {
        yield put(practiceQuestionUpdateFailure());
    }
}

function* getQuestionRequest(action) {
    try {
        const res = yield INTERVIEW.post(`interviews/incbotResponse`, action.payload.data);
        if (res.status === 200 || res.status === 201) {
            yield put(getQuestionSuccess());
            yield call(action.payload.callback, res.data.result);
        }
    } catch (e) {
        yield put(getQuestionFailure());
    }
}

function* submitTranscribeRequest(action) {
    try {
        let res = yield INTERVIEW.post(`interviews/updateTranscript/${action.payload?.roomId}`, action?.payload);
        if (res.status === 200) yield put(submitTranscribeSuccess());
    } catch (e) {
        yield put(submitTranscribeFailure());
    }
}

function* updateCandidateRequest(action) {
    try {
        let res = yield INTERVIEW.post(`interviews/updateCandidate/${action.payload?.roomId}`, action?.payload);
        if (res.status === 200) yield put(updateCandidateSuccess());
    } catch (e) {
        yield put(updateCandidateFailure());
    }
}

function* candidateCallStartRequest(action) {
    try {
        let res = yield INTERVIEW.post(`interviews/callStart/${action.payload.roomId}`);
        if (res.status === 200 || res.status === 201) {
            if (res?.data?.result?.isInterviewCanceled) {
                yield put(candidateCallStartFailure());
                ToastManager.addToast({
                    title: 'This interview has been cancelled',
                });
            } else {
                yield call(
                    action.payload.callback,
                    res?.data?.result?.status,
                    res?.data?.result?.creditExpired,
                    res?.data?.result
                );
                yield put(candidateCallStartSuccess(res.data.result));
                if (!res?.data?.result?.status && !res?.data?.result?.resumeWindowExpired)
                    ToastManager.addToast({
                        title: 'This meeting has already started.',
                    });
            }
        }
    } catch (e) {
        yield put(candidateCallStartFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
        else
            ToastManager.addToast({
                title: 'This meeting has already started.',
            });
    }
}
function* candidateCallEndRequest(action) {
    const { doFinally, endCallStatus, roomId } = action.payload;
    try {
        const res = yield INTERVIEW.post(`interviews/callEnd/${roomId}`, {
            endCallStatus: endCallStatus ?? 'Call End',
        });
        if (res.status === 200 || res.status === 201) {
            yield put(candidateCallEndSuccess());
        }
    } catch (e) {
        yield put(candidateCallEndFailure());
    } finally {
        if (doFinally) doFinally();
    }
}
function* getInterviewRequest(action) {
    try {
        const res = yield INTERVIEW.get(
            `interviews/getInterview/${action.payload.interviewId}/${action.payload.roomId}`
        );
        if (res.status === 200) {
            yield put(getInterviewSuccess(res.data.result));
        } else yield put(getInterviewFailure());
    } catch (e) {
        yield put(getInterviewFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}

function* resendOtpRequest(action) {
    try {
        const res = yield INTERVIEW.get(`interviews/reSendOtp/${action.payload.interviewId}`);
        if (res.status === 200) {
            yield put(resendOtpRequestSuccess(res.data.result));
        }
    } catch (e) {
        yield put(resendOtpRequestFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}
function* verifyRoomRequest(action) {
    try {
        const res = yield INTERVIEW.get(`interviews/verifyRoom/${action.payload.roomId}`);
        if (res.status === 200) {
            yield put(verifyRoomSuccess(res.data.result));
            localStorage.setItem('token', res.data.result?.token || '');
        } else yield put(verifyRoomFailure());
    } catch (e) {
        yield put(verifyRoomFailure());
    }
}
function* verifyCandidateKeyRequest(action) {
    try {
        const res = yield INTERVIEW.get(
            `interviews/verifyCandidate/${action.payload.form.interviewId}/${action.payload.form.key}`
        );
        if (res.status === 200) {
            yield put(verifyCandidateKeySuccess(res.data.result));
            yield call(action.payload.callback, res.data.result?.status);
        }
    } catch (e) {
        yield put(verifyCandidateKeyFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: e?.response?.data?.message,
            });
    }
}

function* fullFaceDetectionNewRequest(action) {
    const { data, onSuccess, onFailure, doFinally } = action.payload ?? {
        data: null,
        onSuccess: () => {},
        onFailure: () => {},
        doFinally: () => {},
    };
    try {
        const res = yield VIDEO.post('ai/precheck-face-detect-mock', data);
        if (res.status === 200) {
            const { detect, face_detection_message } = res.data ?? {
                detect: false,
                face_detection_message: '',
            };
            yield put(fullFaceDetectionNewSuccess(data?.image_base64));
            yield call(onSuccess, detect, face_detection_message);
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (e) {
        yield put(fullFaceDetectionNewFailure(e));
        yield call(onFailure, e);
    } finally {
        if (doFinally) doFinally();
    }
}

function* submitCodingQuestionRequest(action) {
    const { data, onSuccess, onFailure } = action.payload;
    try {
        const res = yield INTERVIEW.post('interviews/performCodeEvaluation', data);
        if (res.status === 200 || res.status === 201) {
            yield put(submitCodingQuestionSuccess());
            if (onSuccess) onSuccess();
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (err) {
        console.error(JSON.stringify(err));
        if (onFailure) onFailure();
        yield put(submitCodingQuestionFailure());
    }
}

function* createCodeSubmissionRequest(action) {
    try {
        const res = yield INTERVIEW.post(
            'interviews/codeCompiler/submit?base64_encoded=true&fields=*&wait=true',
            action.payload.data
        );
        if (res.status === 201 || res.status === 200) {
            yield put(createCodeSubmissionSuccess(res.data));
            yield call(action.payload?.callback, res?.data?.result);
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (e) {
        yield put(createCodeSubmissionFailure());
        ToastManager.addToast({
            title: '400 Bad Request',
        });
    }
}

function* lookingForwardRequest(action) {
    try {
        const res = yield VIDEO.post('ai/mock/analyze', action.payload);
        if (res.status === 200 || res.status === 201) {
            yield put(lookingForwardSuccess());
            yield call(action.payload?.callback, res.data.status);
        } else yield put(lookingForwardFailure());
    } catch (e) {
        yield put(lookingForwardFailure());
    }
}

function* fullFaceDetectionRequest(action) {
    const { data, onSuccess, onFailure, doFinally } = action.payload ?? {
        data: null,
        onSuccess: () => {},
        onFailure: () => {},
        doFinally: () => {},
    };
    try {
        const res = yield VIDEO.post('ai/mock/detect_faces', data);
        if (res.status === 200) {
            const { hasFullFaceDetected } = res.data ?? {
                hasFullFaceDetected: false,
            };
            yield put(fullFaceDetectionSuccess(hasFullFaceDetected));
            yield call(onSuccess, hasFullFaceDetected);
        } else throw new Error(`HTTP error! Status: ${res.status}`);
    } catch (e) {
        yield put(fullFaceDetectionFailure(e));
        yield call(onFailure, e);
    } finally {
        if (doFinally) doFinally();
    }
}

function* fetchScreenShotImageRequest(action) {
    try {
        const res = yield VIDEO.post('incbot/dataURIUpload', action.payload);
        if (res.status === 201) {
            if (action.payload.type !== 10) {
                yield put(fetchScreenShotImageSuccess(res.data.result));
            } else {
                yield call(action.payload.callback, res?.data?.result?.url);
            }
        }
    } catch (e) {
        yield put(fetchScreenShotImageFailure());
    }
}

function* postConsentRequest(action) {
    try {
        const res = yield AUTH.post('permissions/consent', {
            consentType: 'interview_consent',
            userType: 'candidate',
            interviewId: action.payload.interviewId,
        });
        if (res.status === 200 || res.status === 201) {
            yield put(postConsentSuccess());
            yield call(action.payload.callback);
        } else throw new Error(GENERAL_ERROR);
    } catch (e) {
        yield put(postConsentFailure());
        if (e?.response?.data?.message)
            ToastManager.addToast({
                title: GENERAL_ERROR,
            });
    }
}

// Saga watcher function to trigger startMeetingRequest on GET_SIGNATURE action
export function* watchSubmitTranscribeAPI() {
    yield takeEvery(SUBMIT_TRANSCRIBE, submitTranscribeRequest);
}
export function* watchCandidateCallEndAPI() {
    yield takeLeading(CANDIDATE_CALL_END, candidateCallEndRequest);
}
export function* watchResendOtpRequestAPI() {
    yield takeEvery(RESEND_OTP_REQUEST, resendOtpRequest);
}
export function* watchCandidateCallStartAPI() {
    yield takeLeading(CANDIDATE_CALL_START, candidateCallStartRequest);
}
export function* watchGetInterviewAPI() {
    yield takeLeading(GET_INTERVIEW, getInterviewRequest);
}
export function* watchVerifyCandidateKeyAPI() {
    yield takeLeading(VERIFY_CANDIDATE_KEY, verifyCandidateKeyRequest);
}
export function* watchVerifyRoomAPI() {
    yield takeLeading(VERIFY_ROOM, verifyRoomRequest);
}
export function* watchUpdateCandidateAPI() {
    yield takeLeading(UPDATE_CANDIDATE, updateCandidateRequest);
}
export function* watchFullFaceDetectionNewAPI() {
    yield takeLeading(FULL_FACE_DETECTION_NEW, fullFaceDetectionNewRequest);
}
export function* watchSubmitCodingQuestion() {
    yield takeEvery(SUBMIT_CODING_QUESTION, submitCodingQuestionRequest);
}
export function* watchCreateCodeSubmissionAPI() {
    yield takeEvery(CREATE_CODE_SUBMISSION, createCodeSubmissionRequest);
}
export function* watchLookingForwardAPI() {
    yield takeEvery(LOOKING_FORWARD, lookingForwardRequest);
}
export function* watchPracticeQuestionAPI() {
    yield takeLeading(PRACTICE_QUESTION, practiceQuestionRequest);
}
export function* watchPracticeQuestionUpdateAPI() {
    yield takeEvery(PRACTICE_QUESTION_UPDATE, practiceQuestionUpdateRequest);
}
export function* watchFullFaceDetectionAPI() {
    yield takeEvery(FULL_FACE_DETECTION, fullFaceDetectionRequest);
}
export function* watchGetQuestionAPI() {
    yield takeEvery(GET_QUESTION, getQuestionRequest);
}
export function* watchFetchScreenShotImageAPI() {
    yield takeEvery(FETCH_SCREENSHOT_IMAGE, fetchScreenShotImageRequest);
}
export function* watchPostConsentAPI() {
    yield takeLeading(POST_CONSENT, postConsentRequest);
}
export function* watchLiveKitTokenAPI() {
    yield takeLeading(LIVE_KIT_TOKEN, liveKitTokenRequest);
}

export function* watchGetSessionTokenAPI() {
    yield takeLeading(GET_SESSION_TOKEN, getSessionTokenRequest);
}

export default function* rootSaga() {
    yield all([
        watchSubmitTranscribeAPI(),
        watchCandidateCallEndAPI(),
        watchCandidateCallStartAPI(),
        watchGetInterviewAPI(),
        watchVerifyCandidateKeyAPI(),
        watchResendOtpRequestAPI(),
        watchVerifyRoomAPI(),
        watchUpdateCandidateAPI(),
        watchFullFaceDetectionNewAPI(),
        watchSubmitCodingQuestion(),
        watchCreateCodeSubmissionAPI(),
        watchLookingForwardAPI(),
        watchPracticeQuestionAPI(),
        watchPracticeQuestionUpdateAPI(),
        watchFullFaceDetectionAPI(),
        watchGetQuestionAPI(),
        watchFetchScreenShotImageAPI(),
        watchPostConsentAPI(),
        watchLiveKitTokenAPI(),
        watchGetSessionTokenAPI(),
    ]);
}
